import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useCourses } from '../../context/CoursesContext';
import { slugify } from '../../data/courseExtras';
import {
  ChevronDown,
  ChevronRight,
  Monitor,
  Database,
  Coffee,
  Cpu,
  Globe2,
  Bot,
  TrendingUp,
  Terminal,
  ShieldCheck,
  PenTool,
  Palette,
  Megaphone,
  Rocket,
  Code2,
  BarChart3,
  Sparkles,
  Briefcase,
  Award,
  Users,
} from 'lucide-react';
import './Navbar.css';

// ---- Courses mega-menu data ------------------------------------
const COURSE_COLUMNS = [
  {
    key: 'fullstack',
    title: 'Full Stack Development',
    icon: Code2,
    badge: 'Popular',
    subCourses: [
      { label: 'Certified Frontend Development', desc: 'HTML, CSS, JavaScript, React', icon: Monitor, color: 'text-blue-400' },
      { label: 'Certified Backend Development', desc: 'Node.js, Express, MongoDB', icon: Database, color: 'text-emerald-400' },
      { label: 'Certified MERN Stack Development', desc: 'React, Node.js, MongoDB, Express', icon: Code2, color: 'text-amber-400' },
      { label: 'Certified Java Fullstack Development', desc: 'Java, Spring Boot, React, MySQL', icon: Coffee, color: 'text-orange-400' },
    ],
  },
  {
    key: 'aiml',
    title: 'AI / Machine Learning',
    icon: Bot,
    badge: 'Hot',
    subCourses: [
      { label: 'Certified Artificial Intelligence', desc: 'Python, TensorFlow, PyTorch', icon: Bot, color: 'text-purple-400' },
      { label: 'Certified AI + Machine Learning', desc: 'Scikit-learn, TensorFlow, ML', icon: Sparkles, color: 'text-violet-400' },
      { label: 'Certified Generative AI', desc: 'LangChain, OpenAI, Prompt Engineering', icon: Rocket, color: 'text-fuchsia-400' },
      { label: 'Certified Agentic AI', desc: 'LangGraph, CrewAI, Multi-Agent Systems', icon: Cpu, color: 'text-indigo-400' },
    ],
  },
  {
    key: 'data',
    title: 'Data Analyst & Science',
    icon: BarChart3,
    badge: 'In Demand',
    subCourses: [
      { label: 'Certified Data Analyst', desc: 'Excel, SQL, Power BI, Python', icon: Database, color: 'text-amber-400' },
      { label: 'Certified Data Scientist', desc: 'Python, SQL, Machine Learning, Tableau', icon: TrendingUp, color: 'text-orange-400' },
      { label: 'Data Science & Automations', desc: 'Python, Pandas, Selenium, Airflow', icon: Terminal, color: 'text-yellow-400' },
      { label: 'Data Science + AI', desc: 'Python, TensorFlow, SQL, Power BI', icon: Bot, color: 'text-rose-400' },
    ],
  },
  {
    key: 'devops',
    title: 'DevOps & Cloud',
    icon: Globe2,
    subCourses: [
      { label: 'Cloud Solutions & DevOps', desc: 'AWS, Docker, Kubernetes, Terraform', icon: Globe2, color: 'text-sky-400' },
      { label: 'Certified AWS Cloud Practitioner', desc: 'EC2, S3, Lambda, AWS Core', icon: ShieldCheck, color: 'text-blue-400' },
      { label: 'Certified DevOps Engineer', desc: 'Docker, Kubernetes, Jenkins, Git', icon: Terminal, color: 'text-cyan-400' },
      { label: 'Microsoft Azure Cloud Solutions', desc: 'Azure, ARM, DevOps, PowerShell', icon: Database, color: 'text-indigo-400' },
    ],
  },
  {
    key: 'other',
    title: 'Other Courses',
    icon: Palette,
    subCourses: [
      { label: 'UI / UX Design', desc: 'Figma, Wireframes & Prototyping', icon: PenTool, color: 'text-pink-400' },
      { label: 'Digital Marketing', desc: 'SEO, Ads & Social Growth', icon: Megaphone, color: 'text-amber-500' },
      { label: 'Python Programming', desc: 'Scripting, Backend & Automation', icon: Terminal, color: 'text-yellow-400' },
      { label: 'C / C++ Programming', desc: 'DSA & Systems Fundamentals', icon: Cpu, color: 'text-orange-400' },
      { label: 'Mobile App Development', desc: 'Flutter & Cross-platform Apps', icon: Monitor, color: 'text-blue-400' },
      { label: 'Java Programming', desc: 'Core Java to Spring Boot', icon: Coffee, color: 'text-amber-400' },
    ],
  },
];

// ---- Internships mega-menu data --------------------------------
const INTERNSHIP_COLUMNS = [
  {
    key: '3-month',
    title: '3 Month Internship',
    icon: Briefcase,
    items: [
      { num: 1, label: 'Live Client Projects', desc: 'Work on real projects from WeNexa', icon: Code2, color: 'text-blue-400' },
      { num: 2, label: 'Hands-on Experience', desc: 'Build practical skills with mentorship', icon: Award, color: 'text-emerald-400' },
      { num: 3, label: 'Portfolio Building', desc: 'Create projects for your resume', icon: Rocket, color: 'text-purple-400' },
      { num: 4, label: 'Certificate of Completion', desc: 'Recognized internship certificate', icon: Award, color: 'text-amber-400' },
    ],
    duration: '3 months',
  },
  {
    key: '6-month',
    title: '6 Month Internship',
    icon: TrendingUp,
    items: [
      { num: 1, label: 'Advanced Projects', desc: 'Complex real-world implementations', icon: Rocket, color: 'text-purple-400', badge: 'Popular' },
      { num: 2, label: 'Stipend & Earnings', desc: '75% of project value goes to students', icon: TrendingUp, color: 'text-green-400' },
      { num: 3, label: 'Career Support', desc: 'Mentor-guided growth into full-time roles', icon: Users, color: 'text-cyan-400' },
      { num: 4, label: 'Placement Assistance', desc: 'Direct hiring opportunities', icon: Sparkles, color: 'text-pink-400', badge: 'Premium' },
    ],
    duration: '6 months',
  },
];

// Hover-intent timing
const OPEN_DELAY = 60;
const CLOSE_DELAY = 220;

const panelVariants = {
  hidden: { opacity: 0, y: -6, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.18, ease: [0.16, 1, 0.3, 1], staggerChildren: 0.035, delayChildren: 0.03 },
  },
  exit: { opacity: 0, y: -6, scale: 0.98, transition: { duration: 0.12, ease: 'easeIn' } },
};

const columnVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.2, ease: 'easeOut' } },
};

const itemListVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.025, delayChildren: 0.04 } },
};

const itemVariants = {
  hidden: { opacity: 0, x: -6 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.16, ease: [0.16, 1, 0.3, 1] } },
};

const iconChipVariants = {
  rest: { scale: 1 },
  hover: { scale: 1.08, transition: { duration: 0.15, ease: [0.16, 1, 0.3, 1] } },
};

const InternshipsDropdown = ({ onNavigate, id, onMouseEnter, onMouseLeave, onClose, onApplyNow }) => {
  return (
    <motion.div
      id={id}
      role="menu"
      variants={panelVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className="
        absolute left-1/2 top-full z-50 mt-3 flex w-[94vw] max-w-[800px] -translate-x-1/2
        flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white
        shadow-[0_24px_60px_-12px_rgba(0,0,0,0.15)]
        sm:w-[90vw] md:w-[88vw] lg:w-[min(70vw,800px)]
      "
      style={{ maxHeight: 'min(70vh, 540px)' }}
    >
      <motion.div
        className="h-[3px] w-full shrink-0 bg-gradient-to-r from-blue-500/0 via-blue-500 to-blue-500/0"
        initial={{ scaleX: 0, opacity: 0 }}
        animate={{ scaleX: 1, opacity: 1 }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        style={{ transformOrigin: 'center' }}
      />

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="grid grid-cols-1 gap-x-6 gap-y-8 p-5 sm:grid-cols-2 sm:p-6 lg:gap-x-8 lg:p-7">
          {INTERNSHIP_COLUMNS.map((col, colIdx) => (
            <motion.div
              key={col.key}
              variants={columnVariants}
              className={`${colIdx > 0 ? 'sm:border-l sm:border-slate-200 sm:pl-6 lg:pl-8' : ''}`}
            >
              <div className="mb-4 flex items-center gap-2 text-blue-600">
                <col.icon size={15} strokeWidth={2.25} />
                <span className="text-[11px] font-semibold uppercase tracking-[0.12em]">
                  {col.title}
                </span>
                <span className="ml-auto rounded-full bg-blue-100 px-2 py-0.5 text-[9px] font-bold text-blue-700">
                  {col.duration}
                </span>
              </div>

              <motion.div variants={itemListVariants} className="flex flex-col gap-1">
                {col.items.map((item) => (
                  <motion.button
                    key={item.num}
                    variants={itemVariants}
                    initial="rest"
                    whileHover="hover"
                    whileTap={{ scale: 0.985 }}
                    role="menuitem"
                    onClick={() => onNavigate(item, col)}
                    className="
                      group/item relative flex w-full items-start gap-3 rounded-xl px-2.5 py-2.5
                      text-left transition-colors duration-150
                      hover:bg-slate-50 focus-visible:bg-slate-50
                      focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500/50
                    "
                  >
                    <motion.span
                      variants={iconChipVariants}
                      className={`
                        mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg
                        bg-slate-100 ${item.color}
                        group-hover/item:bg-slate-200
                      `}
                      style={{ transition: 'background-color 150ms' }}
                    >
                      <item.icon size={15} strokeWidth={2} />
                    </motion.span>

                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="truncate text-[13.5px] font-medium text-slate-700 group-hover/item:text-slate-900">
                          {item.num}. {item.label}
                        </span>
                        {item.badge && (
                          <span className="shrink-0 rounded-full bg-blue-100 px-1.5 py-[1px] text-[9.5px] font-semibold tracking-wide text-blue-700">
                            {item.badge}
                          </span>
                        )}
                      </span>
                      <span className="mt-0.5 block truncate text-[11.5px] text-slate-500">
                        {item.desc}
                      </span>
                    </span>

                    <ChevronRight
                      size={14}
                      className="mt-1.5 shrink-0 text-slate-300 opacity-0 transition-all duration-150 group-hover/item:translate-x-0.5 group-hover/item:text-slate-500 group-hover/item:opacity-100"
                    />
                  </motion.button>
                ))}
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>

      <motion.div
        variants={columnVariants}
        className="
          flex shrink-0 flex-col items-start justify-between gap-4 border-t border-slate-200
          bg-slate-50 px-5 py-4 sm:flex-row sm:items-center sm:px-6 sm:py-5 lg:px-7
        "
      >
        <div className="flex items-center gap-3">
          <motion.span
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-600"
            animate={{ y: [0, -2, 0] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
          >
            <Briefcase size={16} />
          </motion.span>
          <div>
            <p className="text-[13.5px] font-semibold text-slate-900">
              Ready to start your internship journey?
            </p>
            <p className="text-[11.5px] text-slate-600">
              Gain real-world experience and build your professional portfolio.
            </p>
          </div>
        </div>

        <motion.button
          type="button"
          onClick={onApplyNow}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
          transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="
            group/cta flex w-full shrink-0 items-center justify-center gap-2 rounded-lg bg-blue-500 px-4 py-2.5
            text-[13px] font-semibold text-white shadow-sm shadow-blue-500/20
            transition-all duration-150 hover:bg-blue-600 hover:shadow-md hover:shadow-blue-500/25
            sm:w-auto
          "
        >
          Apply Now
          <ChevronRight size={15} className="transition-transform duration-150 group-hover/cta:translate-x-0.5" />
        </motion.button>
      </motion.div>
    </motion.div>
  );
};

const CoursesDropdown = ({ onNavigate, id, onMouseEnter, onMouseLeave }) => {
  const [activeCategory, setActiveCategory] = useState(COURSE_COLUMNS[0].key);
  const activeCol = COURSE_COLUMNS.find((c) => c.key === activeCategory) || COURSE_COLUMNS[0];

  return (
    <motion.div
      id={id}
      role="menu"
      variants={panelVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className="
        absolute left-1/2 top-full z-50 mt-3 flex w-[95vw] max-w-[1020px] -translate-x-1/2
        overflow-hidden rounded-2xl border border-slate-200/80 bg-white
        shadow-[0_25px_60px_-12px_rgba(0,0,0,0.18)]
      "
      style={{ maxHeight: 'min(82vh, 640px)' }}
    >
      {/* Top gradient line */}
      <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" />

      {/* LEFT SIDE - Categories */}
      <div className="w-[270px] shrink-0 border-r border-slate-100 bg-gradient-to-b from-slate-50 to-white p-5">
        <p className="mb-4 px-1 text-[11px] font-bold uppercase tracking-[0.15em] text-slate-400">
          Categories
        </p>

        <div className="flex flex-col gap-1.5">
          {COURSE_COLUMNS.map((col) => {
            const isActive = activeCategory === col.key;
            return (
              <button
                key={col.key}
                onMouseEnter={() => setActiveCategory(col.key)}
                onClick={() => setActiveCategory(col.key)}
                className={`
                  group relative flex w-full items-center gap-3 rounded-xl px-3.5 py-3 text-left
                  transition-all duration-200
                  ${isActive
                    ? 'bg-white text-blue-700 shadow-md shadow-blue-100/80 ring-1 ring-blue-100'
                    : 'text-slate-600 hover:bg-white/80 hover:text-slate-900 hover:shadow-sm'
                  }
                `}
              >
                <span
                  className={`
                    flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-all
                    ${isActive
                      ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-sm'
                      : 'bg-slate-100 text-slate-500 group-hover:bg-slate-200'
                    }
                  `}
                >
                  <col.icon size={17} strokeWidth={2.2} />
                </span>

                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className={`truncate text-[13.5px] font-semibold ${isActive ? 'text-blue-700' : ''}`}>
                      {col.title}
                    </span>
                    {col.badge && (
                      <span
                        className={`
                          shrink-0 rounded-full px-2 py-0.5 text-[9.5px] font-bold tracking-wide
                          ${col.badge === 'Popular' ? 'bg-orange-100 text-orange-600' : ''}
                          ${col.badge === 'Hot' ? 'bg-rose-100 text-rose-600' : ''}
                          ${col.badge === 'In Demand' ? 'bg-emerald-100 text-emerald-600' : ''}
                        `}
                      >
                        {col.badge}
                      </span>
                    )}
                  </span>
                </span>

                <ChevronRight
                  size={15}
                  className={`shrink-0 transition-all duration-200 ${
                    isActive ? 'translate-x-0.5 text-blue-500' : 'text-slate-300 group-hover:text-slate-400'
                  }`}
                />
              </button>
            );
          })}
        </div>
      </div>

      {/* RIGHT SIDE - Sub Courses */}
      <div className="flex min-w-0 flex-1 flex-col bg-white">
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-slate-100 px-7 py-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-md shadow-blue-200">
            <activeCol.icon size={20} strokeWidth={2.2} />
          </div>
          <div>
            <h3 className="text-[16px] font-bold text-slate-900">{activeCol.title}</h3>
            <p className="text-[12.5px] text-slate-500">
              {activeCol.subCourses.length} professional programs
            </p>
          </div>
        </div>

        {/* Courses Grid */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-6">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {activeCol.subCourses.map((item, idx) => (
              <motion.button
                key={item.label}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: idx * 0.04 }}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => onNavigate(item)}
                className="
                  group/item flex items-start gap-3.5 rounded-2xl border border-slate-100
                  bg-white p-4 text-left shadow-sm
                  transition-all duration-200
                  hover:border-blue-200 hover:bg-gradient-to-br hover:from-blue-50/80 hover:to-indigo-50/50
                  hover:shadow-md hover:shadow-blue-100/50
                "
              >
                <span
                  className={`
                    mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl
                    bg-slate-50 ${item.color}
                    transition-all duration-200
                    group-hover/item:scale-105 group-hover/item:bg-white group-hover/item:shadow-sm
                  `}
                >
                  <item.icon size={18} strokeWidth={2.1} />
                </span>

                <span className="min-w-0 flex-1 pt-0.5">
                  <span className="block text-[14px] font-semibold text-slate-800 leading-snug group-hover/item:text-blue-700">
                    {item.label}
                  </span>
                  <span className="mt-1 block text-[12px] leading-relaxed text-slate-500">
                    {item.desc}
                  </span>
                </span>

                <ChevronRight
                  size={15}
                  className="mt-1.5 shrink-0 text-slate-300 opacity-0 transition-all duration-200 group-hover/item:translate-x-1 group-hover/item:text-blue-500 group-hover/item:opacity-100"
                />
              </motion.button>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-between gap-4 border-t border-slate-100 bg-gradient-to-r from-slate-50 to-blue-50/30 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-100 text-blue-600">
              <Rocket size={18} />
            </div>
            <div>
              <p className="text-[14px] font-semibold text-slate-900">
                Not sure which course to pick?
              </p>
              <p className="text-[12px] text-slate-500">
                We’ll help you find the perfect program.
              </p>
            </div>
          </div>

          <motion.button
            onClick={() => onNavigate({ findMyCourse: true })}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="
              flex shrink-0 items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600
              px-5 py-2.5 text-[13.5px] font-semibold text-white
              shadow-md shadow-blue-200
              transition-all hover:from-blue-700 hover:to-indigo-700 hover:shadow-lg
            "
          >
            Find My Course
            <ChevronRight size={16} />
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

// -----------------------------------------------------------------------------

const Navbar = () => {
  const { user, logout } = useAuth();
  const { activeCourses } = useCourses();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [coursesOpen, setCoursesOpen] = useState(false);
  const [mobileCoursesOpen, setMobileCoursesOpen] = useState(false);
  const [internshipsOpen, setInternshipsOpen] = useState(false);
  const [mobileInternshipsOpen, setMobileInternshipsOpen] = useState(false);
  const [showInternshipModal, setShowInternshipModal] = useState(false);
  const internshipsRef = useRef(null);
  const internshipsCloseTimer = useRef(null);
  const [canHover, setCanHover] = useState(false);
  const coursesRef = useRef(null);
  const coursesTriggerRef = useRef(null);
  const openTimer = useRef(null);
  const closeTimer = useRef(null);

  useEffect(() => {
    if (!showInternshipModal) {
      document.body.style.overflow = '';
    }
  }, [showInternshipModal]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    setCanHover(mq.matches);
    const listener = (e) => setCanHover(e.matches);
    mq.addEventListener ? mq.addEventListener('change', listener) : mq.addListener(listener);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', listener) : mq.removeListener(listener);
    };
  }, []);

  const clearTimers = () => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
  };

  const openCourses = useCallback(() => {
    clearTimers();
    openTimer.current = setTimeout(() => setCoursesOpen(true), OPEN_DELAY);
  }, []);

  const scheduleCloseCourses = useCallback(() => {
    clearTimers();
    closeTimer.current = setTimeout(() => setCoursesOpen(false), CLOSE_DELAY);
  }, []);

  useEffect(() => clearTimers, []);

  useEffect(() => {
    const onClick = (e) => {
      if (coursesRef.current && !coursesRef.current.contains(e.target)) {
        setCoursesOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape' && coursesOpen) {
        clearTimers();
        setCoursesOpen(false);
        coursesTriggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [coursesOpen]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const scrollTo = (id) => {
    setMenuOpen(false);
    setCoursesOpen(false);
    const NAV_H = 74;
    const doScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - NAV_H;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    };
    if (window.location.pathname !== '/') {
      navigate('/');
      setTimeout(doScroll, 400);
    } else {
      doScroll();
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleCourseNavigate = useCallback(
    (item) => {
      clearTimers();
      setCoursesOpen(false);
      setMenuOpen(false);

      if (item.findMyCourse) {
        scrollTo('courses');
        return;
      }

      navigate(`/courses/${slugify(item.label)}`);
    },
    [navigate]
  );

  const handleTriggerClick = () => {
    clearTimers();
    setCoursesOpen((o) => !o);
  };

  const handleInternshipNavigate = useCallback(
    (item, column) => {
      if (internshipsCloseTimer.current) clearTimeout(internshipsCloseTimer.current);
      setInternshipsOpen(false);
      setMenuOpen(false);
      navigate('/internship');
    },
    [navigate]
  );

  const handleInternshipApplyNow = () => {
    setInternshipsOpen(false);
    setMenuOpen(false);
    document.body.style.overflow = 'auto';
    navigate('/internship');
  };

  const handleInternshipSelection = (type) => {
    setShowInternshipModal(false);
    document.body.style.overflow = '';
    navigate(`/internships/${type}`);
  };

  const openInternships = useCallback(() => {
    if (internshipsCloseTimer.current) clearTimeout(internshipsCloseTimer.current);
    setInternshipsOpen(true);
  }, []);

  const scheduleCloseInternships = useCallback(() => {
    if (internshipsCloseTimer.current) clearTimeout(internshipsCloseTimer.current);
    internshipsCloseTimer.current = setTimeout(() => setInternshipsOpen(false), CLOSE_DELAY);
  }, []);

  const NAV_LINKS = [
    { label: 'Courses', id: 'courses', dropdown: true },
    { label: 'Live Projects', id: 'internships', simpleDropdown: true },
    { label: 'Events', id: 'events', isRoute: true },
    { label: 'Blog', id: 'blog', isRoute: true },
    { label: 'About Us', id: 'about', isRoute: true },
    { label: 'Contact', id: 'contact', scrollTo: true },
  ];

  return (
    <nav className={`nav${scrolled ? ' scrolled' : ''}`} id="nav">
      <div className="nav-inner">
        <Link to="/" className="logo-link">
          <img src="/welogo.png" alt="WeIntern" className="nav-logo" />
        </Link>

        <ul className="nav-links">
          {NAV_LINKS.map((l) => {
            if (l.dropdown) {
              return (
                <li
                  key={l.id}
                  ref={coursesRef}
                  className="relative"
                  onMouseEnter={canHover ? openCourses : undefined}
                  onMouseLeave={canHover ? scheduleCloseCourses : undefined}
                >
                  <button
                    ref={coursesTriggerRef}
                    className="nav-link relative flex items-center gap-1"
                    onClick={handleTriggerClick}
                    onFocus={openCourses}
                    aria-expanded={coursesOpen}
                    aria-haspopup="menu"
                    aria-controls="courses-mega-menu"
                  >
                    <span className={coursesOpen ? 'text-[#2373F4]' : ''}>{l.label}</span>
                    <ChevronDown
                      size={14}
                      className={`transition-transform duration-200 ${
                        coursesOpen ? 'rotate-180 text-[#2373F4]' : ''
                      }`}
                    />
                    <motion.span
                      className="absolute -bottom-1 left-0 h-[2px] rounded-full bg-[#2373F4]"
                      initial={false}
                      animate={{
                        width: coursesOpen ? '100%' : '0%',
                        opacity: coursesOpen ? 1 : 0,
                      }}
                      transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                    />
                  </button>
                  <AnimatePresence>
                    {coursesOpen && (
                      <CoursesDropdown
                        id="courses-mega-menu"
                        onNavigate={handleCourseNavigate}
                        onMouseEnter={canHover ? openCourses : undefined}
                        onMouseLeave={canHover ? scheduleCloseCourses : undefined}
                      />
                    )}
                  </AnimatePresence>
                </li>
              );
            }
            if (l.simpleDropdown) {
              return (
                <li key={l.id}>
                  <button
                    className="nav-link"
                    onClick={() => navigate('/internship')}
                  >
                    {l.label}
                  </button>
                </li>
              );
            }
            return (
              <li key={l.id}>
                {l.isRoute ? (
                  <Link to={`/${l.id}`} className="nav-link">
                    {l.label}
                  </Link>
                ) : (
                  <button className="nav-link" onClick={() => scrollTo(l.id)}>
                    {l.label}
                  </button>
                )}
              </li>
            );
          })}
        </ul>

        <div className="nav-ctas">
          {user ? (
            <>
              <div className="nav-user-info">
                <div className="nav-avatar">{user.name?.[0]?.toUpperCase()}</div>
                <span className="nav-user-type">
                  {user.role === 'admin' ? 'Admin' : 'Student'}
                </span>
              </div>
              {user.role === 'admin' ? (
                <Link
                  to="/admin"
                  className="btn btn-outline"
                  style={{ fontSize: '.82rem', padding: '.5rem 1rem' }}
                >
                  ⚙️ Admin
                </Link>
              ) : (
                <Link
                  to="/dashboard"
                  className="btn btn-outline"
                  style={{ fontSize: '.82rem', padding: '.5rem 1rem' }}
                >
                  Dashboard
                </Link>
              )}
              <button
                onClick={handleLogout}
                className="btn btn-outline"
                style={{ fontSize: '.82rem', padding: '.5rem 1rem' }}
              >
                Logout
              </button>
            </>
          ) : (
            <>
              <div className="nav-students-count">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <span>4k+ Students</span>
              </div>
              <div className="nav-for-biz">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 7H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z" />
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                </svg>
                <span>For Businesses</span>
              </div>
              <Link to="/login" className="btn-nav-login">
                Login / Sign Up
              </Link>
            </>
          )}
        </div>

        <button
          className={`hamburger${menuOpen ? ' open' : ''}`}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <span />
          <span />
          <span />
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="mobile-menu overflow-y-auto"
            style={{ maxHeight: 'calc(100vh - 74px)' }}
          >
            {NAV_LINKS.map((l) => {
              if (l.dropdown) {
                return (
                  <div key={l.id} className="flex flex-col">
                    <button
                      className="mobile-nav-link inline-flex items-center justify-between"
                      onClick={() => setMobileCoursesOpen((o) => !o)}
                      aria-expanded={mobileCoursesOpen}
                    >
                      {l.label}
                      <motion.span
                        animate={{ rotate: mobileCoursesOpen ? 180 : 0 }}
                        transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                        className="flex"
                      >
                        <ChevronDown size={16} />
                      </motion.span>
                    </button>
                    <AnimatePresence>
                      {mobileCoursesOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.18, ease: 'easeInOut' }}
                          className="overflow-hidden px-3 pb-4 pt-1 sm:px-4"
                        >
                          <div className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-3 sm:p-4">
                            {COURSE_COLUMNS.map((col) => (
                              <div key={col.key}>
                                <div className="mb-2 flex items-center gap-2 text-[#2373F4]">
                                  <col.icon size={14} strokeWidth={2.25} />
                                  <span className="text-[11px] font-semibold uppercase tracking-wider">
                                    {col.title}
                                  </span>
                                </div>
                                <div className="flex flex-col gap-1.5">
                                  {col.subCourses.map((item) => (
                                    <button
                                      key={item.label}
                                      onClick={() => handleCourseNavigate(item)}
                                      className="flex items-center gap-2.5 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-left text-sm text-slate-800 active:bg-slate-100"
                                    >
                                      <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-slate-100 ${item.color}`}>
                                        <item.icon size={14} />
                                      </span>
                                      <span className="min-w-0 flex-1 truncate">{item.label}</span>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            ))}
                            <button
                              onClick={() => handleCourseNavigate({ findMyCourse: true })}
                              className="flex items-center justify-center gap-2 rounded-lg bg-[#2373F4] px-4 py-2.5 text-sm font-semibold text-white"
                            >
                              <Rocket size={15} />
                              Find My Course
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              }
              if (l.simpleDropdown) {
                return (
                  <button
                    key={l.id}
                    className="mobile-nav-link"
                    onClick={() => {
                      setMenuOpen(false);
                      navigate('/internship');
                    }}
                  >
                    {l.label}
                  </button>
                );
              }
              return l.isRoute ? (
                <Link
                  key={l.id}
                  to={`/${l.id}`}
                  className="mobile-nav-link"
                  onClick={() => setMenuOpen(false)}
                >
                  {l.label}
                </Link>
              ) : (
                <button
                  key={l.id}
                  className="mobile-nav-link"
                  onClick={() => scrollTo(l.id)}
                >
                  {l.label}
                </button>
              );
            })}
            {user ? (
              <>
                {user.role === 'admin' ? (
                  <Link
                    to="/admin"
                    onClick={() => setMenuOpen(false)}
                    className="mobile-nav-link"
                  >
                    Admin Panel
                  </Link>
                ) : (
                  <Link
                    to="/dashboard"
                    onClick={() => setMenuOpen(false)}
                    className="mobile-nav-link"
                  >
                    Dashboard
                  </Link>
                )}
                <button
                  onClick={() => {
                    handleLogout();
                    setMenuOpen(false);
                  }}
                  className="mobile-cta-btn"
                >
                  Logout
                </button>
              </>
            ) : (
              <Link
                to="/login"
                onClick={() => setMenuOpen(false)}
                className="mobile-cta-btn"
              >
                Login / Sign Up
              </Link>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;